/**
 * <seasonal-products>: the product area of a seasonal page (feature 018,
 * specs/018-seasonal-landing-pages/contracts/seasonal-landing-page.md §3, research R6).
 * A page cannot filter or sort a collection, so every other state is rendered by the category's own
 * collection address: /collections/{handle}?section_id=seasonal-page-products&…, whose
 * <seasonal-products> content replaces this element's content.
 * - Category tiles ([data-seasonal-categories] [data-category]) switch the area in place; without
 *   JavaScript they stay links to the collection pages.
 * - Filters, chips and sort stay with Dawn's facets.js, which asks this page's current category
 *   (facets.js getRenderPath) and keeps ?category= in the address.
 * - The address (?category=…&filter…&sort_by=…) is restored on load and on Back / Forward. Liquid cannot
 *   read query parameters on a page, so the server always renders the first, "All" state.
 */
if (!customElements.get('seasonal-products')) {
  class SeasonalProducts extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-seasonal-products]');
      if (!this.root || this.initialised) return;
      this.initialised = true;
      this.linked = this.root.dataset.category;
      this.allowed = (this.root.dataset.categories || this.linked).split(',');
      this.linkedLabel = this.querySelector('.collection-grid-header__title')?.textContent.trim() || '';
      this.status = this.root.querySelector('[data-seasonal-status]');
      // The first history entry has no state; Back to it restores the address the page was opened with
      const opened = new URLSearchParams(window.location.search);
      opened.delete('view');
      if (!opened.has('category')) opened.set('category', this.linked);
      this.initialSearch = opened.toString();

      document.addEventListener('click', (event) => this.onTileClick(event));
      // Capture phase: runs before facets.js's own popstate listener, which then skips the entry
      window.addEventListener('popstate', (event) => this.onHistoryChange(event), { capture: true });
      this.restoreFromAddress();
    }

    get current() {
      return this.root.dataset.category;
    }

    tiles() {
      return [...document.querySelectorAll('[data-seasonal-categories] [data-category]')];
    }

    labelFor(handle) {
      const tile = this.tiles().find((element) => element.dataset.category === handle);
      return tile?.dataset.label || (handle === this.linked ? this.linkedLabel : handle);
    }

    // Filters and sort of an address, without the page-level keys
    static stateParams(search) {
      const params = new URLSearchParams(search);
      ['category', 'view', 'page', 'section_id'].forEach((key) => params.delete(key));
      return params.toString();
    }

    onTileClick(event) {
      const tile = event.target.closest('[data-seasonal-categories] [data-category]');
      if (!tile || event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!this.allowed.includes(tile.dataset.category)) return;
      event.preventDefault();
      if (this.loading) return;
      this.switchCategory(tile.dataset.category, '', { push: true, fallbackHref: tile.href });
    }

    async switchCategory(handle, params, { push = false, fallbackHref = null, quiet = false } = {}) {
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      this.style.minHeight = `${this.offsetHeight}px`;
      try {
        const query = ['section_id=seasonal-page-products', params].filter(Boolean).join('&');
        const response = await fetch(`${this.root.dataset.collectionsPath}${handle}?${query}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const area = html.querySelector('seasonal-products');
        if (!area) throw new Error('No product area in the response');

        this.innerHTML = area.innerHTML;
        this.root.dataset.category = handle;
        const label = this.labelFor(handle);
        const title = this.querySelector('.collection-grid-header__title');
        if (title) title.textContent = label;
        this.markTiles(handle);

        const searchParams = ['category=' + handle, params].filter(Boolean).join('&');
        if (push) this.pushAddress(searchParams);
        if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = searchParams;
        if (!quiet) this.announce(label);
      } catch (error) {
        if (fallbackHref) window.location.href = fallbackHref;
      } finally {
        this.loading = false;
        this.removeAttribute('aria-busy');
        requestAnimationFrame(() => (this.style.minHeight = ''));
      }
    }

    markTiles(handle) {
      this.tiles().forEach((tile) => {
        const isCurrent = tile.dataset.category === handle;
        tile.classList.toggle('is-current', isCurrent);
        if (isCurrent) tile.setAttribute('aria-current', 'true');
        else tile.removeAttribute('aria-current');
      });
    }

    pushAddress(searchParams) {
      const view = new URLSearchParams(window.location.search).get('view');
      const query = [view ? 'view=' + encodeURIComponent(view) : '', searchParams].filter(Boolean).join('&');
      history.pushState({ searchParams }, '', `${window.location.pathname}?${query}`);
    }

    announce(label) {
      if (!this.status) return;
      const count = Number(this.querySelector('[data-products-count]')?.dataset.productsCount || 0);
      const template = count === 1 ? this.root.dataset.statusOne : this.root.dataset.statusOther;
      this.status.textContent = (template || '').replace('[count]', count).replace('[title]', label);
    }

    restoreFromAddress() {
      const wanted = new URLSearchParams(window.location.search).get('category');
      const category = this.allowed.includes(wanted) ? wanted : this.current;
      const params = SeasonalProducts.stateParams(window.location.search);
      if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsInitial = this.initialSearch;
      if (category !== this.current || params) {
        this.switchCategory(category, params);
      } else if (this.querySelector('[data-filters-pending]')) {
        // Feature 022: the page render has no filters (Shopify lists them only on the collection's own address);
        // the collection address render has them. Same content otherwise, so nothing is announced.
        this.switchCategory(category, '', { quiet: true });
      }
    }

    onHistoryChange(event) {
      const searchParams = event.state?.searchParams ?? this.initialSearch;
      const wanted = new URLSearchParams(searchParams).get('category');
      const category = this.allowed.includes(wanted) ? wanted : this.linked;
      if (category === this.current) return; // same category: facets.js re-renders filters and sort
      if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = searchParams;
      this.switchCategory(category, SeasonalProducts.stateParams(searchParams));
    }
  }

  customElements.define('seasonal-products', SeasonalProducts);
}
